import clsx from "clsx"
import { Children, Fragment, forwardRef } from "react"

import { Divider } from "@atoms"

import styles from "./SidePanelNavigation.module.scss"
import type { SidePanelNavigationRootProps, SidePanelNavigationRowProps } from "./SidePanelNavigation.types"

/**
 * Render the optional navigation area between the header and body.
 *
 * Takes one `SidePanel.NavigationRow` for single-level navigation, or two for two-level navigation:
 * the top-level row first, then the row for the selected object's sections.
 */
function SidePanelNavigationRoot(props: SidePanelNavigationRootProps, ref: React.Ref<HTMLDivElement>) {
    const { children, className, ...rest } = props

    // A divider follows every row, so two-level navigation gets one between the levels too.
    // toArray drops empty children, so a conditionally rendered row leaves no stray divider.
    return (
        <div ref={ref} className={clsx(styles.base, className)} {...rest}>
            {Children.toArray(children).map((row, index) => (
                <Fragment key={index}>
                    {row}
                    <Divider />
                </Fragment>
            ))}
        </div>
    )
}

function SidePanelNavigationRow(props: SidePanelNavigationRowProps, ref: React.Ref<HTMLDivElement>) {
    const { children, className, ...rest } = props

    return (
        <div ref={ref} className={clsx(styles.row, className)} {...rest}>
            {children}
        </div>
    )
}

export default {
    Root: forwardRef(SidePanelNavigationRoot),
    Row: forwardRef(SidePanelNavigationRow),
}
